import {faEye, faEyeSlash} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {AlertFallback} from '@rdcomponents/alert-fallback/alert-fallback';
import {Badge, Button} from 'antd';
import {useMemo, useState} from 'react';

import {PageCard} from '../../../../layout/page-card/page-card';
import {Spinner} from '../../../../layout/spinner/spinner';
import {LocalePlural} from '../../../../provider/locale/locale-plural';
import {Locale} from '../../../../provider/locale/localization';
import {useDashboardRecommendations} from '../../../../service/dashboard/dashboard-recommendations/dashboard-recommendations-hook';
import {
    DashboardRecommendationItemType,
    DashboardRecommendationTypeEnum,
} from '../../../../service/dashboard/dashboard-recommendations/dashboard-recommendations-type';
import {getEnumValue} from '../../../../util/enum';

import {DashboardRecommendationItem} from './dashboard-recommendation-item';
import {EmptyRecommendationsList} from './empty-recommendations-list/empty-recommendations-list';
import * as styles from './dashboard-recommendations.scss';

type PropsType = {
    companiesCount?: number;
};

export function DashboardRecommendations(props: PropsType): JSX.Element {
    const {companiesCount} = props;
    const {result, processError, isInProgress, onPostponeChange} = useDashboardRecommendations();
    const [isCollapsed, setIsCollapsed] = useState(true);

    const recommendations: Array<DashboardRecommendationItemType> = useMemo(() => {
        return (
            result
                ?.filter((item) => {
                    return Boolean(getEnumValue(DashboardRecommendationTypeEnum, item.key));
                })
                ?.filter((item: DashboardRecommendationItemType) => {
                    if (isCollapsed) {
                        return item.count && !item.postponed;
                    }

                    return true;
                })
                .sort((item1: DashboardRecommendationItemType, item2: DashboardRecommendationItemType) => {
                    return (item1.postponed || 0) - (item2.postponed || 0);
                }) || []
        );
    }, [isCollapsed, result]);

    if (processError) {
        return <AlertFallback />;
    }

    if (isInProgress || !result) {
        return <Spinner />;
    }

    const activeRecommendationsCount = result.filter((item) => item.count && !item.postponed).length;

    const hasPostponedItems = result.length > activeRecommendationsCount;

    return (
        <PageCard
            bodyClassName={styles.DashboardRecommendations_cardBody}
            className={styles.DashboardRecommendations_card}
            title={
                <div className={styles.DashboardRecommendations_headerWrapper}>
                    <span className={styles.DashboardRecommendations_title}>
                        <LocalePlural
                            count={companiesCount ?? 1}
                            fewKey="DASHBOARD_PAGE__RECOMMENDATION__TITLE__FEW"
                            manyKey="DASHBOARD_PAGE__RECOMMENDATION__TITLE__MANY"
                            singularKey="DASHBOARD_PAGE__RECOMMENDATION__TITLE__SINGULAR"
                        />
                    </span>
                    <Badge
                        className={styles.DashboardRecommendations_headerBadge}
                        count={activeRecommendationsCount}
                        showZero={false}
                    />
                </div>
            }
        >
            <div className={styles.DashboardRecommendations_cardsWrapper}>
                {recommendations.map((recommendation, index) => {
                    return (
                        <DashboardRecommendationItem
                            key={recommendation.key}
                            onPostponeChange={onPostponeChange}
                            recommedation={recommendation}
                            showBottomBorder={recommendations.length - 1 !== index}
                        />
                    );
                })}

                {recommendations.length === 0 && <EmptyRecommendationsList />}

                {hasPostponedItems && (
                    <Button
                        className={styles.DashboardRecommendations_collapseButton}
                        icon={<FontAwesomeIcon icon={isCollapsed ? faEyeSlash : faEye} />}
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        type="text"
                    >
                        {isCollapsed ? (
                            <Locale stringKey="DASHBOARD_PAGE__RECOMMENDATION__SHOW_POSTPONED" />
                        ) : (
                            <Locale stringKey="DASHBOARD_PAGE__RECOMMENDATION__HIDE_POSTPONED" />
                        )}
                    </Button>
                )}
            </div>
        </PageCard>
    );
}
